import { saveAs } from 'file-saver';
import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  AlignmentType,
} from 'docx';

import moment from 'moment';
import { MONTH_GENITIVE } from '../../../../../assets/const';

// ─── Константы ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'first_passed', text: 'установлена первая квалификационная категория' },
  { value: 'higher_passed', text: 'установлена высшая квалификационная категория' },
  { value: 'first_failed', text: 'отказано в установлении первой квалификационной категории' },
  { value: 'higher_failed', text: 'отказано в установлении высшей квалификационной категории' },
];

// ─── Утилиты ──────────────────────────────────────────────────────────────────

const formatDate = (date) => moment(date).format(`DD ${MONTH_GENITIVE[moment(date).month()]} YYYY`);
const formatDateNew = (date) => moment(date).format(`"___" ${MONTH_GENITIVE[moment(date).month()]} YYYY`);

const createTextParagraph = (text, options = {}) => {
  const {
    bold = false,
    size,
    alignment = AlignmentType.LEFT,
    indentLeft = 0,
    indentFirstLine = 0,
    spacingAfter = 300,
    font = 'Times New Roman',
    rightAligned = false,
  } = options;

  const safeText = text || "";

  const textRunOptions = {
    text: safeText,
    bold,
    font: { name: font },
  };

  if (size != null && !isNaN(size) && size > 0 && size <= 100) {
    textRunOptions.size = size * 2;
  }

  const children = [new TextRun(textRunOptions)];

  if (rightAligned) {
    children.unshift(new TextRun({ text: "\t", font: { name: font } }));
  }

  const indent = {};
  if (indentLeft > 0) indent.left = indentLeft;
  if (indentFirstLine !== 0) indent.firstLine = indentFirstLine;

  return new Paragraph({
    children,
    alignment: rightAligned ? AlignmentType.RIGHT : alignment,
    indent: Object.keys(indent).length > 0 ? indent : undefined,
    spacing: { after: spacingAfter },
    tabStops: rightAligned ? [{ position: 9000 }] : undefined,
  });
};

const createJustifiedParagraph = (text, bold = false) => {
  return createTextParagraph(text, {
    bold,
    alignment: AlignmentType.JUSTIFIED,
    indentFirstLine: 720,
  });
};

const createCenteredParagraph = (text, bold = true, size = 16) => {
  return createTextParagraph(text, {
    bold,
    size,
    alignment: AlignmentType.CENTER,
  });
};

const createPageBreak = () =>
  new Paragraph({
    children: [
      new TextRun({
        text: "\u00A0",
        font: { name: "Times New Roman" },
      }),
    ],
    pageBreakBefore: true,
    spacing: { before: 0, after: 0 },
  });

// ─── Основная функция ─────────────────────────────────────────────────────────

const getDecreeGukDocx = async ({ data, comission }) => {
  const dateEndSimple = formatDateNew(comission?.date_to);
  const dateEndComission = formatDate(comission?.date_to);

  const content = [];

  // === ЗАГОЛОВОК ПРИКАЗА ===

  content.push(
    createCenteredParagraph('ПРИКАЗ', true, 46),
    createCenteredParagraph('НАЧАЛЬНИКА ГЛАВНОГО УПРАВЛЕНИЯ КАДРОВ', true, 16),
    createCenteredParagraph('МИНИСТЕРСТВА ОБОРОНЫ РОССИЙСКОЙ ФЕДЕРАЦИИ', true, 16),
    createCenteredParagraph(`${dateEndSimple}г. № _____`, false, 16),
    createCenteredParagraph('г. Москва', false, 16),

    createCenteredParagraph('О квалификационных категориях', true, 16),
    createCenteredParagraph('педагогических работников организаций, находящихся в ведении', true, 16),
    createCenteredParagraph('Министерства обороны Российской Федерации и осуществляющих', true, 16),
    createCenteredParagraph('образовательную деятельность', true, 16)
  );

  // === ОБОСНОВАНИЕ ===

  content.push(
    createJustifiedParagraph(
      'В целях установления соответствия уровня квалификации педагогических работников организаций, находящихся в ведении Министерства обороны Российской Федерации и осуществляющих образовательную деятельность, требованиям, предъявляемым к квалификационным категориям (первой или высшей), в соответствии с частью 3 статьи 49 Федерального закона от 29 декабря 2012 г. № 273-ФЗ «Об образовании в Российской Федерации», приказом Министерства просвещения Российской Федерации от 24 марта 2023г.'
    ),
    createJustifiedParagraph(
      '№ 196 «Об утверждении Порядка проведения аттестации педагогических работников организаций, осуществляющих образовательную деятельность», приказом Министра обороны Российской Федерации от 16 сентября 2011 г.'
    ),
    createJustifiedParagraph(
      '№ 1650 «Об аттестационных комиссиях Министерства обороны Российской Федерации по аттестации педагогических и руководящих работников федеральных государственных образовательных учреждений Министерства обороны Российской Федерации» и Инструкцией по проведению аттестации педагогических работников организаций, находящихся в ведении Министерства обороны Российской Федерации и осуществляющих образовательную деятельность, утвержденной статс-секретарем – заместителем Министра обороны Российской Федерации 22 июня 2023 г.,'
    )
  );

  // === ТЕЛО ПРИКАЗА ===

  content.push(
    createTextParagraph('ПРИКАЗЫВАЮ:', true, { indentLeft: 720, spacingAfter: 400 }),
    createJustifiedParagraph(`1. Установить c ${dateEndComission}г.:`),
    createJustifiedParagraph(
      'первую квалификационную категорию педагогическим работникам организаций, находящихся в ведении Министерства обороны Российской Федерации и осуществляющих образовательную деятельность, согласно списку (приложение № 1 к настоящему приказу);'
    ),
    createJustifiedParagraph(
      'высшую квалификационную категорию педагогическим работникам организаций, находящихся в ведении Министерства обороны Российской Федерации и осуществляющих образовательную деятельность, согласно списку (приложение № 2 к настоящему приказу).'
    ),
    createJustifiedParagraph(
      '2. Контроль за выполнением настоящего приказа возложить на начальника управления военного образования – заместителя начальника Главного управления кадров Министерства обороны Российской Федерации.'
    )
  );

  // === ПОДПИСЬ ===

  content.push(
    createCenteredParagraph('ВРЕМЕННО ИСПОЛНЯЮЩИЙ ОБЯЗАННОСТИ', true, 16),
    createCenteredParagraph('НАЧАЛЬНИКА ГЛАВНОГО УПРАВЛЕНИЯ КАДРОВ', true, 16),
    createCenteredParagraph('МИНИСТЕРСТВА ОБОРОНЫ РОССИЙСКОЙ ФЕДЕРАЦИИ', true, 16),
    createCenteredParagraph('генерал-лейтенант', true, 16),
    createTextParagraph('Ю. Бобров', { rightAligned: true, spacingAfter: 600 })
  );

  // === ДИНАМИЧЕСКИЕ ПРИЛОЖЕНИЯ ===

  CATEGORIES.forEach((category, index) => {
    // Проверяем, есть ли данные для этой категории
    let hasContent = false;
    if (data.data && data.data[category.value]) {
      for (const school of Object.keys(data.data[category.value])) {
        if (data.data[category.value][school]?.length > 0) {
          hasContent = true;
          break;
        }
      }
    }

    if (!hasContent) return;

    // Добавляем разрыв страницы
    content.push(createPageBreak());

    // Заголовок приложения
    content.push(
      createTextParagraph(`Приложение № ${index + 1}`, { alignment: AlignmentType.RIGHT }),
      createTextParagraph('к приказу начальника Главного управления кадров', { alignment: AlignmentType.RIGHT }),
      createTextParagraph('Министерства обороны Российской Федерации', { alignment: AlignmentType.RIGHT }),
      createTextParagraph(`${dateEndSimple}г. № _____`, { alignment: AlignmentType.RIGHT, spacingAfter: 600 }),

      createCenteredParagraph('СПИСОК', true, 14),
      createCenteredParagraph('педагогических работников', true, 14),
      createCenteredParagraph('организаций, находящихся в ведении Министерства обороны', true, 14),
      createCenteredParagraph('Российской Федерации и осуществляющих образовательную', true, 14),
      createCenteredParagraph(`деятельность, которым ${category.text}`, true, 14)
    );

    // Списки по школам
    if (data.data && data.data[category.value]) {
      Object.keys(data.data[category.value]).forEach((school) => {
        const items = data.data[category.value][school] || [];
        if (!items.length) return;

        content.push(createCenteredParagraph(school, true, 14));

        items.forEach((item, ind) => {
          const fio = `${item.full_user_name || ''}, ${item.attestation_name || ''}.`;
          content.push(
            createTextParagraph(`${ind + 1}. ${fio}`, {
              indentLeft: 720,
              spacingAfter: 200,
            })
          );
        });
      });
    }

    // Подчерк в конце приложения
    content.push(
      createCenteredParagraph('___________', false, 12)
    );
  });

  // Создаём документ
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: "Times New Roman",
            size: 24, // 12pt
          },
        },
      },
    },
    sections: [
      {
        children: content,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  return blob;
};

export default getDecreeGukDocx;